import type { Migration } from '../types';

// ── Mock feature flags (DEV ONLY) ─────────────────────────────────────────────
// Flip exactly ONE to true at a time to exercise each Attention modal scenario.
// All default to false — no effect in Test or Prod.
// ─────────────────────────────────────────────────────────────────────────────
export const SHOW_MOCK_SCHEDULED_MIGRATION = false;           // Completed scheduled migration with history
export const SHOW_MOCK_FAILED_SCHEDULED_JOBS = false;         // Failed scheduled snapshot + migration (attention modal)
export const SHOW_MOCK_EXECUTION_FAILURE = false;             // Execution failure (failurePhase = 2) — red banner + rollback CTAs

/**
 * Returns an array of mock Migration objects for local development testing.
 * Controlled entirely by the flags above — returns [] in all non-Dev scenarios
 * where no flag is flipped.
 */
export function buildMockJobs(): Migration[] {
  const mocks: Migration[] = [];

  // Mock 1: Completed scheduled migration with history
  if (SHOW_MOCK_SCHEDULED_MIGRATION) {
    mocks.push({
      id: 'MOCK-SCHEDULED-MIGRATION-001',
      name: '[MOCK] Weekly Production Sync',
      customerId: 'mock-customer',
      createdBy: 'mock-user@example.com',
      createdAtUtc: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAtUtc: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      sourceOrgId: 'mock-source-org-id',
      sourceOrgName: 'Mock Source Org',
      targetOrgId: 'mock-target-org-id',
      targetOrgName: 'Mock Target Org',
      status: 'completed',
      resources: ['genesyscloud_user::user1', 'genesyscloud_group::group1', 'genesyscloud_queue::queue1'],
      isSnapshot: false,
      exportAll: false,
      includeDependencies: true,
      snapshotCronExpression: '0 7 * * 1,3,5', // Monday, Wednesday, Friday at 7:00 AM UTC
      isActive: true,
      snapshotHistory: [
        {
          capturedAtUtc: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260613T070000Z',
          fileCount: 12,
          totalBytes: 1536000
        },
        {
          capturedAtUtc: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260610T070000Z',
          fileCount: 11,
          totalBytes: 1468000
        },
        {
          capturedAtUtc: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Failed',
          blobPath: 'mock/path/20260608T070000Z',
          fileCount: 0,
          totalBytes: 0
        }
      ]
    });
  }

  // Mock 2: Failed scheduled snapshot (attention modal)
  if (SHOW_MOCK_FAILED_SCHEDULED_JOBS) {
    mocks.push({
      id: 'MOCK-FAILED-SNAPSHOT-001',
      name: '[MOCK] Failed Scheduled Snapshot',
      customerId: 'mock-customer',
      createdBy: 'mock-user@example.com',
      createdAtUtc: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAtUtc: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      sourceOrgId: 'mock-source-org-id',
      sourceOrgName: 'Mock Source Org',
      targetOrgId: 'mock-target-org-id',
      targetOrgName: 'Mock Target Org',
      status: 'exportfailed',
      resources: ['genesyscloud_auth_role::role1', 'genesyscloud_routing_queue::queue1'],
      isSnapshot: true,
      exportAll: false,
      includeDependencies: true,
      snapshotCronExpression: '0 2 * * 0', // Sundays at 2:00 AM UTC
      isActive: true,
      snapshotHistory: [
        {
          capturedAtUtc: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260608T020000Z',
          fileCount: 8,
          totalBytes: 1024000
        },
        {
          capturedAtUtc: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260601T020000Z',
          fileCount: 8,
          totalBytes: 998400
        }
      ],
      job1Export: {
        errors: ['Rate limit exceeded: Too many API requests', 'Failed to export genesyscloud_auth_role resources']
      }
    });

    // Mock 3: Failed scheduled migration (attention modal)
    mocks.push({
      id: 'MOCK-FAILED-MIGRATION-001',
      name: '[MOCK] Failed Scheduled Migration',
      customerId: 'mock-customer',
      createdBy: 'mock-user@example.com',
      createdAtUtc: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAtUtc: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      sourceOrgId: 'mock-source-org-id',
      sourceOrgName: 'Mock Source Org',
      targetOrgId: 'mock-target-org-id',
      targetOrgName: 'Mock Target Org',
      status: 'ImportFailed',
      resources: ['genesyscloud_user::user1', 'genesyscloud_group::group1'],
      isSnapshot: false,
      exportAll: false,
      includeDependencies: true,
      snapshotCronExpression: '0 9 * * 1-5', // Monday–Friday at 9:00 AM UTC
      isActive: true,
      job1Export: {
        status: 'Complete',
        artifactCount: 23,
        startedAtUtc: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        completedAtUtc: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
        success: true
      },
      job2Transform: {
        status: 'Complete',
        success: true
      },
      job3Import: {
        status: 'Failed',
        errors: [
          'Terraform apply failed: Error creating genesyscloud_user "john.doe@example.com": API Error 409 Conflict — user already exists in target org',
          '2 of 8 resources were applied before failure'
        ],
        failurePhase: 2,
        completedAtUtc: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
        success: false
      },
      snapshotHistory: [
        {
          capturedAtUtc: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260614T090000Z',
          fileCount: 15,
          totalBytes: 2048000
        },
        {
          capturedAtUtc: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Complete',
          blobPath: 'mock/path/20260613T090000Z',
          fileCount: 14,
          totalBytes: 1945600
        },
        {
          capturedAtUtc: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Failed',
          blobPath: 'mock/path/20260612T090000Z',
          fileCount: 0,
          totalBytes: 0
        }
      ]
    });
  }

  // Mock: Execution failure (failurePhase = 2) — red danger banner + Compare Snapshots / Initiate Rollback CTAs
  if (SHOW_MOCK_EXECUTION_FAILURE) {
    mocks.push({
      id: 'MOCK-EXECUTION-FAILURE-001',
      name: '[MOCK] Execution Failure Migration',
      customerId: 'mock-customer',
      createdBy: 'mock-user@example.com',
      createdAtUtc: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      updatedAtUtc: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
      sourceOrgId: 'mock-source-org-id',
      sourceOrgName: 'Mock Source Org',
      targetOrgId: 'mock-target-org-id',
      targetOrgName: 'Mock Target Org',
      status: 'ImportFailed',
      resources: ['genesyscloud_routing_queue', 'genesyscloud_user', 'genesyscloud_group'],
      isSnapshot: false,
      exportAll: false,
      includeDependencies: true,
      isActive: true,
      job3Import: {
        errors: [
          'Terraform apply failed: Error creating genesyscloud_routing_queue "Sales Queue": API Error 409 Conflict — resource already exists',
          '3 of 12 resources were applied before failure'
        ],
        failurePhase: 2,
        completedAtUtc: new Date(Date.now() - 20 * 60 * 1000).toISOString()
      }
    });
  }

  return mocks;
}

